import { lazy, Suspense, useEffect } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
// QueryClientProvider is handled by PersistQueryClientProvider in main.tsx
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AdminRoute } from "@/components/auth/AdminRoute";
import { ProtectedRoute } from "@/components/auth/ProtectedRoute";
import { RoleGate } from "@/components/auth/RoleGate";
import { AuthProvider } from "@/contexts/AuthContext";
import { PWAPrompt } from "@/components/PWAPrompt";
import { OnboardingProvider } from "@/components/onboarding/OnboardingProvider";
import { AppLayout } from "@/components/layout/AppLayout";
import { useCustomDomain } from "@/hooks/useCustomDomain";
import { ConfirmDialogProvider } from "@/components/ui/confirm-dialog";
// queryClient is managed in main.tsx via PersistQueryClientProvider

// ========== SITE INSTITUCIONAL (Público) ==========
const SmartLanding = lazy(() => import("./pages/SmartLanding"));
const AcademyLanding = lazy(() => import("./pages/AcademyLanding"));
const AcademyLandingByDomain = lazy(() => import("./pages/AcademyLandingByDomain"));
const Blog = lazy(() => import("./pages/Blog"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const TermsOfService = lazy(() => import("./pages/TermsOfService"));
const ExclusaoDeConta = lazy(() => import("./pages/ExclusaoDeConta"));

// ========== APP ==========
const Auth = lazy(() => import("./pages/Auth"));
const Timer = lazy(() => import("./pages/Timer"));
const AppIndex = lazy(() => import("./pages/AppIndex"));
const Agenda = lazy(() => import("./pages/Agenda"));
const Alunos = lazy(() => import("./pages/Alunos"));
const Treinos = lazy(() => import("./pages/Treinos"));
const Configuracoes = lazy(() => import("./pages/Configuracoes"));
const Financeiro = lazy(() => import("./pages/Financeiro"));
const Gestao = lazy(() => import("./pages/Gestao"));
const Conta = lazy(() => import("./pages/Conta"));
const CadastroAluno = lazy(() => import("./pages/CadastroAluno"));
const CadastroAlunoPersonalizado = lazy(() => import("./pages/CadastroAlunoPersonalizado"));
const Cockpit = lazy(() => import("./pages/Cockpit"));

// ========== PREMIUM / ADMIN ==========
const Admin = lazy(() => import("./pages/Admin"));
const AdminLogin = lazy(() => import("./pages/admin/AdminLogin"));
const ContentEditor = lazy(() => import("./pages/admin/ContentEditor"));
const LandingPageManager = lazy(() => import("./pages/admin/LandingPageManager"));
// JediLab route now redirects to Admin
const Coach = lazy(() => import("./pages/Coach"));
const CoachSettings = lazy(() => import("./pages/CoachSettings"));
const Upgrade = lazy(() => import("./pages/Upgrade"));
const WhatsApp = lazy(() => import("./pages/WhatsApp"));

const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const NotFound = lazy(() => import("./pages/NotFound"));

// queryClient is now defined in src/lib/queryClient.ts

// Inner component that has access to hooks inside QueryClientProvider
const AppRoutes = () => {
  const { isCustomDomain, academyId, isLoading: domainLoading, notFound } = useCustomDomain();
  
  // Detect recovery errors in URL hash (e.g. from expired/consumed OTP links)
  // and redirect to /reset-password with error context
  useEffect(() => {
    const params = new URLSearchParams(window.location.hash.slice(1));
    const isOtpExpired =
      params.get('error') === 'access_denied' &&
      params.get('error_code') === 'otp_expired';
    if (isOtpExpired) {
      const path = window.location.pathname;
      // Only redirect if we're NOT already on /reset-password
      if (path !== '/reset-password') {
        window.location.replace('/reset-password?link_error=expired');
      }
    }
  }, []);

  // Custom domain loading state
  if (domainLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-pulse text-foreground">Carregando...</div>
      </div>
    );
  }

  // Custom domain detected but not found in DB
  if (notFound) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <h1 className="text-4xl font-bold text-foreground mb-2">Domínio não configurado</h1>
          <p className="text-muted-foreground">Este domínio ainda não está vinculado a nenhuma academia.</p>
        </div>
      </div>
    );
  }

  // Determine if we should show the academy landing (custom domain + root path only)
  const pathname = typeof window !== 'undefined' ? window.location.pathname : '/';
  const showCustomLanding = isCustomDomain && academyId && pathname === '/';

  // Both platform and custom domain use the same auth-wrapped routing
  return (
    <AuthProvider>
      <OnboardingProvider>
        
      <Suspense fallback={<div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-pulse text-foreground">Carregando...</div>
      </div>}>
        <Routes>
          {/* ========== SITE INSTITUCIONAL (Público) ========== */}
          <Route path="/" element={
            showCustomLanding ? (
              <AcademyLandingByDomain academyId={academyId!} />
            ) : (
              <SmartLanding />
            )
          } />
          <Route path="/a/:slug" element={<AcademyLanding />} />
          <Route path="/blog" element={<Blog />} />
          <Route path="/privacy" element={<PrivacyPolicy />} />
          <Route path="/terms" element={<TermsOfService />} />
          <Route path="/exclusao-de-conta" element={<ExclusaoDeConta />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/cadastro-aluno" element={<CadastroAluno />} />
          <Route path="/c/:teacherCode" element={<CadastroAlunoPersonalizado />} />

          {/* ========== AUTH ========== */}
          <Route path="/app/auth" element={<Auth />} />
          {/* Alias legado — quem digitar /auth cai no fluxo do app */}
          <Route path="/auth" element={<Navigate to="/app/auth" replace />} />
          
          {/* ========== APP PÚBLICO (PLG - Fase 2) ========== */}
          <Route path="/app" element={<AppLayout><AppIndex /></AppLayout>} />
          <Route path="/app/timer" element={<AppLayout><Timer /></AppLayout>} />
          <Route path="/app/cockpit" element={
            <ProtectedRoute gate="gestao">
              <RoleGate allowed={['admin', 'gestor', 'professor']}>
                <AppLayout><Cockpit /></AppLayout>
              </RoleGate>
            </ProtectedRoute>
          } />
          
          <Route path="/app/treinos" element={<AppLayout><Treinos /></AppLayout>} />
          
          {/* ========== APP COM LANDING STATES (PLG - Fase 3-4) ========== */}
          <Route path="/app/agenda" element={<AppLayout><Agenda /></AppLayout>} />
          <Route path="/app/alunos" element={
            <ProtectedRoute gate="alunos">
              <RoleGate allowed={['admin', 'gestor', 'professor']}>
                <AppLayout><Alunos /></AppLayout>
              </RoleGate>
            </ProtectedRoute>
          } />
          
          {/* ========== APP FREEMIUM (Requer Login) ========== */}
          <Route path="/app/conta" element={
            <ProtectedRoute>
              <Conta />
            </ProtectedRoute>
          } />
          <Route path="/app/configuracoes" element={
            <ProtectedRoute>
              <AppLayout><Configuracoes /></AppLayout>
            </ProtectedRoute>
          } />
          <Route path="/app/financeiro" element={
            <ProtectedRoute gate="financeiro">
              <RoleGate allowed={['admin', 'gestor', 'professor']}>
                <AppLayout><Financeiro /></AppLayout>
              </RoleGate>
            </ProtectedRoute>
          } />
          <Route path="/app/gestao" element={
            <ProtectedRoute gate="gestao">
              <RoleGate allowed={['admin', 'gestor', 'professor']}>
                <AppLayout><Gestao /></AppLayout>
              </RoleGate>
            </ProtectedRoute>
          } />
          
          {/* ========== PREMIUM (Sagat + Automação) ========== */}
          <Route path="/app/upgrade" element={<AppLayout><Upgrade /></AppLayout>} />
          <Route path="/app/whatsapp" element={
            <ProtectedRoute>
              <RoleGate allowed={['admin', 'gestor', 'professor']}>
                <AppLayout><WhatsApp /></AppLayout>
              </RoleGate>
            </ProtectedRoute>
          } />
          <Route path="/app/coach" element={
            <ProtectedRoute>
              <RoleGate allowed={['admin', 'gestor', 'professor']}>
                <AppLayout><Coach /></AppLayout>
              </RoleGate>
            </ProtectedRoute>
          } />
          
          {/* ========== ADMIN ========== */}
          <Route path="/admin/login" element={<AdminLogin />} />
          <Route path="/admin" element={
            <AdminRoute>
              <Admin />
            </AdminRoute>
          } />
          <Route path="/admin/content" element={
            <AdminRoute>
              <ContentEditor />
            </AdminRoute>
          } />
          <Route path="/admin/landing-pages" element={
            <AdminRoute>
              <LandingPageManager />
            </AdminRoute>
          } />
          <Route path="/admin/coach" element={
            <AdminRoute>
              <Coach />
            </AdminRoute>
          } />
          <Route path="/admin/coach/settings" element={
            <AdminRoute>
              <CoachSettings />
            </AdminRoute>
          } />
          <Route path="/admin/jedi-lab" element={
            <AdminRoute>
              <Navigate to="/admin" replace />
            </AdminRoute>
          } />
          
          {/* ========== CATCH-ALL ========== */}
          <Route path="*" element={<NotFound />} />
        </Routes>
      </Suspense>
      </OnboardingProvider>
    </AuthProvider>
  );
};

const App = () => (
  <TooltipProvider>
    <ConfirmDialogProvider>
      <Toaster />
      <Sonner />
      <PWAPrompt />
      <BrowserRouter>
        <AppRoutes />
      </BrowserRouter>
    </ConfirmDialogProvider>
  </TooltipProvider>
);

export default App;
